import React, { useEffect, useState } from "react";
import { Col, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import { useSnackbar } from 'v3/components/Snackbar';
import api from 'services/api';
import CheckBox from 'components/CheckBox';

import {
  Select,
  Text,
  Button,
  Modal,
} from 'v3/components'

import SelectBusinessGroup from 'v3/components/SelectBusinessGroup';

import {
  fetchCostCenter,
  fetchCustomers,
  fetchUsers,
  fetchUsersByIds
} from 'utils/fetches';

import { forSelectBusinessGroups } from 'v3/pages/BusinessGroup/Services';

export default function AdvancedFilterModal({
  onChange,
  loading,
  showAdvancedFiltersModal,
  setShowAdvancedFiltersModal,
  setQtdAdvancedFilters,
  setAdvancedFilters,
  advancedFilters
}) {

  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const snackbar = useSnackbar();

  const [provincesOptions, setProvincesOptions] = useState([]);

  useEffect(() => {
    if (!showAdvancedFiltersModal) return;

    const nextSearch = query.toString();
    const currentSearch = (location.search || '').replace(/^\?/, '');
    if (nextSearch === currentSearch) return;

    const timeout = setTimeout(() => {
      navigate({ search: nextSearch }, { replace: true });
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [advancedFilters, showAdvancedFiltersModal, navigate, query, location.search]);

  useEffect(() => {
    const isNotEmpty = value =>
      value !== null && value !== undefined && value !== '' && value !== 0;

    const validateAdvancedFilters = {
      businessGroups: advancedFilters?.businessGroups?.length,
      clients: advancedFilters?.clients?.length,
      users: advancedFilters?.users?.length,
      costCenters: advancedFilters?.costCenters?.length,
      initialProvince: advancedFilters?.initialProvince?.id,
      lastProvince: advancedFilters?.lastProvince?.id,
      expired: advancedFilters?.expired === true ? true : null,
    };

    setQtdAdvancedFilters(
      Object.values(validateAdvancedFilters).filter(isNotEmpty).length
    );
  }, [advancedFilters]);

  const handlerAdvancedFilters = () => {
    onChange(advancedFilters)
    setShowAdvancedFiltersModal(false);
  }


  async function fetchProvinces() {
    try {
      const response = await api.get('provinces');
      const provinces = (response.data || []).map((province) => ({
        ...province,
        label: `${province.uf} (${province.name})`,
        value: province.id,
      }));
      setProvincesOptions(provinces);
    } catch (error) {
      snackbar.show(<Text>Erro ao carregar estados (UF)</Text>, { type: 'error' });
    }
  }


  async function handleInitializeParams() {

    if (query.has('initialProvince')) {
      const initialProvince = provincesOptions.find(
        (p) => p.value == query.get('initialProvince'),
      );
      if (initialProvince) {
        setAdvancedFilters(old => ({ ...old, initialProvince }));
      }
    }

    if (query.has('lastProvince')) {
      const lastProvince = provincesOptions.find(
        (p) => p.value == query.get('lastProvince'),
      );
      if (lastProvince) {
        setAdvancedFilters(old => ({ ...old, lastProvince }));
      }
    }

    if (query.has('clients[]')) {
      const clientsIds = query.getAll('clients[]').map(client => Number(client));
      const allClients = await fetchCustomers({ ids: clientsIds });
      setAdvancedFilters(old => ({ ...old, clients: allClients }));
    }

    if (query.has('users[]')) {
      const usersIds = query.getAll('users[]').map(user => Number(user));
      const { users } = await fetchUsersByIds(usersIds);
      setAdvancedFilters(old => ({ ...old, users }));
    }

    if (query.has('costCenters[]')) {
      const costCentersIds = query.getAll('costCenters[]').map(cc => Number(cc));
      const allCostCenters = await fetchCostCenter({ ids: costCentersIds });
      setAdvancedFilters(old => ({ ...old, costCenters: allCostCenters }));
    }

    if (query.has('businessGroupId[]')) {
      const businessGroupIds = query.getAll('businessGroupId[]').map(id => Number(id));
      const groups = await forSelectBusinessGroups({  ids: businessGroupIds });
      if (groups.length) {
        setAdvancedFilters(old => ({ ...old, businessGroups: groups }));
      }
    }

    if (query.has('expired')) {
      setAdvancedFilters(old => ({ ...old, expired: true }));
    } else {
      setAdvancedFilters(old => ({ ...old, expired: false }));
    }
  }

  useEffect(() => {
    fetchProvinces();
  }, []);

  useEffect(() => {
    if (provincesOptions.length > 0) {
      handleInitializeParams();
    }
  }, [provincesOptions]);

  return (
    <>
      <Modal
        heading={
          <Text color="dark" type="header">
            Filtro Avançado
          </Text>
        }
        show={showAdvancedFiltersModal}
        handleClose={() => { setShowAdvancedFiltersModal(false) }}
        body={
          <Row>
            <Col md={6} xl={6}>
              <SelectBusinessGroup
                label={'Grupos Empresariais'}
                placeholder={'Selecione um grupo...'}
                value={advancedFilters.businessGroups}
                onChange={value => {
                  query.delete('businessGroupId[]');
                  if (value?.length) {
                    value.forEach(v => {
                      query.append('businessGroupId[]', v.id);
                    })
                  }
                  setAdvancedFilters(old => ({ ...old, businessGroups: value || [] }));
                }}
                onSearch={search => forSelectBusinessGroups({ search })}
                multiple
              />
            </Col>
            <Col md={6} xl={6}>
              <Select.Async
                label={'Clientes'}
                multiple
                horizontal
                onSearch={search => fetchCustomers({ search })}
                value={advancedFilters.clients}
                onChange={value => {
                  query.delete('clients[]');
                  if (value?.length) {
                    value.forEach(v => {
                      query.append('clients[]', v.id);
                    })
                  }
                  setAdvancedFilters(old => ({ ...old, clients: value }));
                }}
                getOptionLabel={option => {
                  let label = `${option?.social_name} - ${option?.cgccpf}`;
                  if (option.city) {
                    label += ` - ${option?.city}`;
                  }
                  return label;
                }}
                getOptionValue={option => option.id}
              />
            </Col>
            <Col md={6} xl={6}>
              <Select.Async
                label={'Usuários'}
                multiple
                horizontal
                onSearch={search => fetchUsers(search)}
                value={advancedFilters.users}
                onChange={value => {
                  query.delete('users[]');
                  if (value?.length) {
                    value.forEach(v => {
                      query.append('users[]', v.id);
                    })
                  }
                  setAdvancedFilters(old => ({ ...old, users: value }));
                }}
                getOptionLabel={option => option.username}
                getOptionValue={option => option.id}
              />
            </Col>
            <Col md={6} xl={6}>
              <Select.Async
                multiple
                horizontal
                label={'Centro de Custo'}
                onSearch={search => fetchCostCenter({ search })}
                value={advancedFilters.costCenters}
                onChange={value => {
                  query.delete('costCenters[]');
                  if (value?.length) {
                    value.forEach(v => {
                      query.append('costCenters[]', v.id);
                    })
                  }
                  setAdvancedFilters(old => ({
                    ...old,
                    costCenters: value,
                  }));
                }}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
              />
            </Col>
            <Col md={6} xl={6}>
              <Select
                label="Estado de Origem"
                options={provincesOptions}
                horizontal
                modalHeading="Selecione um estado de origem"
                modalBodyTitle="Estados de origem:"
                placeholder="Selecione..."
                getOptionLabel={option => `${option?.label}`}
                getOptionValue={option => option?.value}
                value={advancedFilters?.initialProvince}
                onChange={value => {
                  if (value) {
                    query.set('initialProvince', value.id);
                  } else {
                    query.delete('initialProvince');
                  }
                  setAdvancedFilters(old => ({ ...old, initialProvince: value }));
                }}
              />
            </Col>
            <Col md={6} xl={6}>
              <Select
                label="Estado de Destino"
                options={provincesOptions}
                horizontal
                modalHeading="Selecione um estado destino"
                modalBodyTitle="Estados destino:"
                placeholder="Selecione..."
                getOptionLabel={option => `${option?.label}`}
                getOptionValue={option => option?.value}
                value={advancedFilters?.lastProvince}
                onChange={value => {
                  if (value) {
                    query.set('lastProvince', value.id);
                  } else {
                    query.delete('lastProvince');
                  }
                  setAdvancedFilters(old => ({ ...old, lastProvince: value }));
                }}
              />
            </Col>
            <Col md={6} xl={6} className="mt-4">
              <CheckBox
                label="Cargas vencidas"
                labelStyle={{ marginBottom: '8px' }}
                checked={!!advancedFilters?.expired}
                onChange={(e) => {
                  const checked = e?.target?.checked;
                  if (checked) {
                    query.set('expired', '1');
                  } else {
                    query.delete('expired');
                  }
                  setAdvancedFilters(old => ({ ...old, expired: checked }));
                }}
              />
            </Col>
          </Row>
        }
        footer={
          <Row>
            <Col>
              <Button
                size="sm"
                onClick={() => handlerAdvancedFilters()}
                disabled={loading}
              >
                <Text weight={500}>Filtrar</Text>
              </Button>
            </Col>
          </Row>
        }
      />
    </>
  );
}
